import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal {...props} />;
}

function DialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			className={cn("fixed inset-0 z-0 bg-black/50", "filter-none!", className)}
			{...props}
		/>
	);
}

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
	hideCloseButton?: boolean;
}

function DialogContent({
	className,
	children,
	hideCloseButton = false,
	...props
}: DialogContentProps) {
	return (
		<DialogPortal>
			{/* Jeden portal i osobna warstwa dla kazdego okna: tlo zawsze pod trescia,
			    takze przy potwierdzeniu otwartym nad innym dialogiem. */}
			<div className="relative z-50">
				<DialogOverlay />
				<DialogPrimitive.Content
					data-slot="dialog-content"
					className={cn(
						"fixed left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2",
						"w-full max-w-md bg-card border border-border rounded-lg shadow-lg",
						"flex flex-col max-h-[calc(100vh-4rem)]",
						// Animacje tw-animate-css nie powinny nadawac tresci filtra blur.
						"filter-none!",
						"data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
						"data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
						"outline-none focus:outline-none",
						// Telefon (<md): arkusz od dolu, do pelnej wysokosci; stopka zostaje
						// przyklejona, bo DialogBody przewija sie sam. Klasy szerokosci/wysokosci
						// od wywolujacego przegrywaja z max-md (wariant jest pozniej w CSS).
						"max-md:inset-x-0 max-md:top-auto max-md:bottom-0 max-md:left-0 max-md:mx-auto max-md:translate-x-0 max-md:translate-y-0",
						"max-md:w-full max-md:max-w-none max-md:max-h-[calc(100dvh-0.5rem)] max-md:rounded-b-none max-md:rounded-t-xl max-md:border-x-0 max-md:border-b-0 max-md:shadow-none",
						"max-md:data-[state=open]:[--tw-enter-translate-x:0] max-md:data-[state=open]:[--tw-enter-translate-y:100%] max-md:data-[state=open]:[--tw-enter-scale:1]",
						"max-md:data-[state=closed]:[--tw-exit-translate-x:0] max-md:data-[state=closed]:[--tw-exit-translate-y:100%] max-md:data-[state=closed]:[--tw-exit-scale:1]",
						className,
					)}
					{...props}
				>
					{children}
					{!hideCloseButton && (
						<DialogPrimitive.Close
							aria-label="Zamknij"
							className={cn(
								"absolute right-3 top-3 z-10",
								"inline-flex h-7 w-7 items-center justify-center rounded-md max-md:right-2 max-md:top-2 max-md:size-9",
								"text-muted-foreground hover:text-foreground hover:bg-accent",
								"transition-colors",
								"focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
								"disabled:pointer-events-none",
							)}
						>
							<X className="h-4 w-4" aria-hidden="true" />
						</DialogPrimitive.Close>
					)}
				</DialogPrimitive.Content>
			</div>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(
				"flex flex-col gap-1 px-6 pt-6 pb-4 pr-12 shrink-0 max-md:px-4 max-md:pt-4 max-md:pb-3 max-md:pr-12",
				className,
			)}
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(
				"flex items-center justify-end gap-2 px-6 py-4 border-t border-border shrink-0",
				// Telefon: glowne akcje 44 px na cala szerokosc, nad strefa gestow.
				"max-md:flex-wrap max-md:px-4 max-md:pt-2.5 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))] max-md:[&>button]:h-11 max-md:[&>button]:min-w-0 max-md:[&>button]:flex-1",
				className,
			)}
			{...props}
		/>
	);
}

function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("px-6 py-4 overflow-y-auto min-h-0 flex-1 max-md:px-4", className)}
			{...props}
		/>
	);
}

interface DialogPaginationProps {
	offset: number;
	limit: number;
	total: number;
	onOffsetChange: (next: number) => void;
	disabled?: boolean;
	className?: string;
}

function DialogPagination({
	offset,
	limit,
	total,
	onOffsetChange,
	disabled = false,
	className,
}: DialogPaginationProps) {
	const hasPrev = offset > 0 && !disabled;
	const hasNext = offset + limit < total && !disabled;
	const from = total === 0 ? 0 : offset + 1;
	const to = Math.min(offset + limit, total);

	if (total <= limit) {
		return null;
	}

	return (
		<div
			className={cn(
				"flex items-center justify-between gap-2 px-6 py-3 border-t border-border shrink-0",
				"text-sm text-muted-foreground",
				className,
			)}
		>
			<button
				type="button"
				disabled={!hasPrev}
				onClick={() => onOffsetChange(Math.max(0, offset - limit))}
				className={cn(
					"inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm font-medium",
					"border border-border bg-transparent hover:bg-accent text-foreground",
					"transition-colors disabled:opacity-40 disabled:pointer-events-none",
				)}
				aria-label="Poprzednia strona"
			>
				<ChevronLeft className="h-4 w-4" aria-hidden="true" />
				Poprzednia
			</button>
			<span className="tabular-nums" aria-live="polite">
				{from}–{to} z {total}
			</span>
			<button
				type="button"
				disabled={!hasNext}
				onClick={() => onOffsetChange(offset + limit)}
				className={cn(
					"inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm font-medium",
					"border border-border bg-transparent hover:bg-accent text-foreground",
					"transition-colors disabled:opacity-40 disabled:pointer-events-none",
				)}
				aria-label="Nastepna strona"
			>
				Następna
				<ChevronRight className="h-4 w-4" aria-hidden="true" />
			</button>
		</div>
	);
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			className={cn("text-base font-semibold text-foreground leading-tight", className)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			className={cn("text-sm text-muted-foreground leading-relaxed", className)}
			{...props}
		/>
	);
}

export type ConfirmVariant = "default" | "destructive" | "warning";

interface ConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description?: string;
	children?: React.ReactNode;
	variant?: ConfirmVariant;
	confirmLabel?: string;
	cancelLabel?: string;
	isLoading?: boolean;
	confirmDisabled?: boolean;
	onConfirm: () => void;
}

const confirmButtonClass: Record<ConfirmVariant, string> = {
	default:
		"bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50",
	destructive:
		"bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50",
	warning:
		"bg-status-warning-bg text-status-warning-text border border-status-warning-border hover:opacity-80 h-8 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50",
};

const iconForVariant: Record<ConfirmVariant, React.ReactNode> = {
	default: null,
	destructive: (
		<svg
			className="w-5 h-5 text-destructive shrink-0 mt-0.5"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			aria-hidden="true"
			role="presentation"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.75}
				d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
			/>
		</svg>
	),
	warning: (
		<svg
			className="w-5 h-5 text-status-warning-text shrink-0 mt-0.5"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			aria-hidden="true"
			role="presentation"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.75}
				d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
			/>
		</svg>
	),
};

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	children,
	variant = "default",
	confirmLabel = "Potwierdź",
	cancelLabel = "Anuluj",
	isLoading = false,
	confirmDisabled = false,
	onConfirm,
}: ConfirmDialogProps) {
	const icon = iconForVariant[variant];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<div className={cn("flex gap-3", icon ? "items-start" : "items-center")}>
						{icon}
						<div className="flex flex-col gap-1 min-w-0">
							<DialogTitle>{title}</DialogTitle>
							{description && <DialogDescription>{description}</DialogDescription>}
						</div>
					</div>
					{children && <div className="mt-2 text-sm text-foreground">{children}</div>}
				</DialogHeader>
				<DialogFooter>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
						className="h-8 px-4 rounded-md text-sm font-medium border border-border bg-transparent hover:bg-accent text-foreground transition-colors disabled:opacity-50"
					>
						{cancelLabel}
					</button>
					<button
						type="button"
						onClick={() => {
							onConfirm();
						}}
						disabled={isLoading || confirmDisabled}
						className={confirmButtonClass[variant]}
					>
						{isLoading ? "Ładowanie…" : confirmLabel}
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

const DialogClose = DialogPrimitive.Close;

export {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPagination,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
